import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CircleHelp, Download, Eye, Pencil } from 'lucide-react';
import styles from './gerador.module.scss';
import Logo from '../home/src/logo.png';
import { getSlugFromHostname } from '../../config/domainMap.ts';
import { createExamplePage, PLACEHOLDER_PROFILE, applyStyleFromBanner } from './utils.ts';
import type { BannerConfig, PageConfig } from './types.ts';
import { BannerFields, BannerList, PageFields } from './Editor.tsx';
import { Preview } from './Preview.tsx';
import { Tour } from './Tour.tsx';
import { TOUR_STEPS } from './tourSteps.ts';
import { downloadBioZip } from './exportZip.ts';

const FONT_HREF =
    'https://fonts.googleapis.com/css2?family=Anton&family=Poppins:wght@400;500;600;700&display=swap';

const Gerador: React.FC = () => {
    const customDomain = Boolean(getSlugFromHostname());
    const [page, setPage] = useState<PageConfig>(() => ({
        ...createExamplePage(),
        profileImage: PLACEHOLDER_PROFILE,
    }));
    const [selectedId, setSelectedId] = useState('exemplo-whatsapp');
    const [tourOpen, setTourOpen] = useState(false);
    const [tourStep, setTourStep] = useState(0);
    const [sawTour, setSawTour] = useState(() => localStorage.getItem('linksvibe-gerador-tour') === '1');
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');
    const [sheetOpen, setSheetOpen] = useState(false);

    useEffect(() => {
        if (document.getElementById('gerador-fonts')) return;
        const link = document.createElement('link');
        link.id = 'gerador-fonts';
        link.rel = 'stylesheet';
        link.href = FONT_HREF;
        document.head.appendChild(link);
    }, []);

    const selected = useMemo(
        () => page.banners.find((b) => b.id === selectedId) ?? page.banners[0],
        [page.banners, selectedId],
    );

    const updateBanner = (banner: BannerConfig) => {
        setPage((current) => ({
            ...current,
            banners: current.banners.map((item) => (item.id === banner.id ? banner : item)),
        }));
    };

    const replicateStyle = () => {
        if (!selected) return;
        setPage((current) => ({
            ...current,
            banners: current.banners.map((item) =>
                item.id === selected.id ? item : applyStyleFromBanner(selected, item),
            ),
        }));
    };

    const startTour = () => {
        setSheetOpen(true);
        setTourStep(0);
        setTourOpen(true);
        const first = TOUR_STEPS[0];
        if (first?.selectBanner && page.banners.some((b) => b.id === first.selectBanner)) {
            setSelectedId(first.selectBanner);
        }
        if (first) {
            window.dispatchEvent(new CustomEvent('gerador-tour-step', { detail: first.target }));
        }
    };

    const closeTour = () => {
        setTourOpen(false);
        setSawTour(true);
        localStorage.setItem('linksvibe-gerador-tour', '1');
    };

    const goTour = (index: number) => {
        setTourStep(index);
        const current = TOUR_STEPS[index];
        if (!current) return;
        if (current.selectBanner && page.banners.some((b) => b.id === current.selectBanner)) {
            setSelectedId(current.selectBanner);
        }
        window.dispatchEvent(new CustomEvent('gerador-tour-step', { detail: current.target }));
    };

    const handleDownload = async () => {
        setError('');
        if (!page.profileImage) {
            setError('Envie uma foto de perfil antes de baixar. Ela aparece sempre acima dos banners.');
            return;
        }
        const missing = page.banners.find((b) => {
            if (!b.title.trim()) return true;
            if (b.coupon.enabled) return !b.coupon.siteUrl.trim();
            return !b.href.trim();
        });
        if (missing) {
            setSelectedId(missing.id);
            setError('Cada banner precisa de título e de um link — ou de um cupom com marca/site.');
            return;
        }
        setDownloading(true);
        try {
            await downloadBioZip(page);
        } catch {
            setError('Não foi possível gerar o ZIP. Tente de novo com imagens menores.');
        } finally {
            setDownloading(false);
        }
    };

    if (customDomain) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className={styles.gerador}>
            <header className={styles.header}>
                <a href="/">
                    <img src={Logo} alt="Links Vibe" className={styles.logo} />
                </a>
                <h1 className={styles.headerTitle}>Gerador de link na bio</h1>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${sawTour ? '' : styles.iconBtnPulse}`}
                        data-tour="ajuda"
                        aria-label="Abrir tutorial"
                        onClick={startTour}
                    >
                        <CircleHelp size={22} />
                    </button>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        data-tour="download"
                        onClick={() => void handleDownload()}
                        disabled={downloading}
                    >
                        <Download size={16} />
                        {downloading ? 'Gerando...' : 'Baixar ZIP'}
                    </button>
                </div>
            </header>

            {error ? <p className={styles.error}>{error}</p> : null}

            <div className={styles.workspace}>
                <aside className={`${styles.editor} ${sheetOpen ? styles.editorOpen : ''}`}>
                    <button
                        type="button"
                        className={styles.sheetGrabber}
                        onClick={() => setSheetOpen((open) => !open)}
                        aria-label={sheetOpen ? 'Fechar editor' : 'Abrir editor'}
                    >
                        <span />
                    </button>
                    <PageFields page={page} onChange={setPage} />
                    <BannerList
                        page={page}
                        selectedId={selected.id}
                        onChange={setPage}
                        onSelect={setSelectedId}
                    />
                    {selected ? (
                        <BannerFields
                            banner={selected}
                            onChange={updateBanner}
                            onReplicateStyle={replicateStyle}
                            canReplicate={page.banners.length > 1}
                        />
                    ) : null}
                    <button
                        type="button"
                        className={styles.ghostBtn}
                        onClick={() => {
                            const next = createExamplePage();
                            next.profileImage = PLACEHOLDER_PROFILE;
                            setPage(next);
                            setSelectedId('exemplo-whatsapp');
                            setError('');
                        }}
                    >
                        Restaurar exemplo inicial
                    </button>
                </aside>
                <div className={styles.stage}>
                    <Preview
                        page={page}
                        selectedId={selected.id}
                        onSelectBanner={(id) => {
                            setSelectedId(id);
                            setSheetOpen(true);
                        }}
                    />
                </div>
            </div>

            <div className={styles.mobileBar}>
                <button
                    type="button"
                    className={sheetOpen ? styles.ghostBtn : styles.primaryBtn}
                    onClick={() => setSheetOpen((open) => !open)}
                >
                    {sheetOpen ? <Eye size={18} /> : <Pencil size={18} />}
                    {sheetOpen ? 'Ver página' : 'Editar'}
                </button>
                <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={() => void handleDownload()}
                    disabled={downloading}
                >
                    <Download size={16} />
                    {downloading ? 'Gerando...' : 'Baixar ZIP'}
                </button>
            </div>

            <Tour
                open={tourOpen}
                stepIndex={tourStep}
                steps={TOUR_STEPS}
                onNext={() => goTour(Math.min(tourStep + 1, TOUR_STEPS.length - 1))}
                onPrev={() => goTour(Math.max(tourStep - 1, 0))}
                onClose={closeTour}
            />
        </div>
    );
};

export default Gerador;
